import { ClientOnly } from '../../../utils/client-only';
import { css } from '@compiled/react';
import moment from 'moment';
import { useIntl, FormattedMessage } from 'react-intl';

import { Provider } from 'react-redux';

import { media } from '@atlaskit/primitives';

import { SpotlightStatic } from '@/components/product-demos/jsw-interactive-board/src/common/spotlight-static.tsx';

import { AvatarGroupStack } from './common/avatar-group-stack';
import { CompleteDemoView } from './common/demo-complete-modal';
import { RestartButton } from './common/restart-button';
import { RoadmapColumn } from './common/roadmap-column';
import { RoadmapGrid } from './common/roadmap-grid';
import { SpotlightView } from './common/spotlight';
import { messages } from './messages';
import defaultStore from './state/store';

export type JswInteractiveRoadmapProps = {
	readonly initialMonth?: number | undefined;
	readonly store?: typeof defaultStore | undefined;
};

export const TEST_IDS = {
	header0: 'epic-header',
	header1: 'month-1-header',
	header2: 'month-2-header',
	header3: 'month-3-header',
};

/**
 * Represents a component for the JSW Interactive Roadmap.
 * @returns A JSX component for the JSW Interactive Roadmap.
 */
export const JswInteractiveRoadmap = ({
	initialMonth,
	store = defaultStore,
}: JswInteractiveRoadmapProps) => {
	const intl = useIntl();
	const locale = intl.locale;
	const epicHeader = <FormattedMessage {...messages.epicHeader} />;

	const date = initialMonth
		? moment().set('month', initialMonth).locale(locale)
		: moment().locale(locale);

	const month1 = <span>{date.format('MMM')}</span>;
	const month2 = <span>{date.add(1, 'M').format('MMM')}</span>;
	const month3 = <span>{date.add(1, 'M').format('MMM')}</span>;

	return (
		<ClientOnly>
			<Provider store={store}>
				<div css={MainContentWrapper} id="main-content-wrapper-jsw-interactive-roadmap">
					<div css={HeaderWrapper}>
						<AvatarGroupStack />
						<RestartButton />
					</div>
					<SpotlightView />
					<div css={JswRoadmapTable}>
						<div css={JswRoadmapHeader}>
							<RoadmapColumn headerId={TEST_IDS.header0} columnIndex={0}>
								{epicHeader}
							</RoadmapColumn>
							<RoadmapColumn headerId={TEST_IDS.header1} columnIndex={1}>
								{month1}
							</RoadmapColumn>
							<RoadmapColumn headerId={TEST_IDS.header2} columnIndex={2}>
								{month2}
							</RoadmapColumn>
							<RoadmapColumn headerId={TEST_IDS.header3} columnIndex={3}>
								{month3}
							</RoadmapColumn>
						</div>
						{/* Grid layout to display epics and stories */}
						<RoadmapGrid />
					</div>
					<CompleteDemoView />
				</div>
				<div css={staticImageContainer}>
					{/* eslint-disable-next-line no-restricted-syntax */}
					<img
						alt={'Preview of Jira Software Roadmaps'}
						src="https://wac-cdn.atlassian.com/misc-assets/webp-images/interactiveTimeline.webp"
					/>
					<div css={staticSpotlightContainer}>
						<SpotlightStatic />
					</div>
				</div>
			</Provider>
		</ClientOnly>
	);
};

const MainContentWrapper = css({
	position: 'relative',
	backgroundColor: '#FFFFFF',
	padding: '26px 0px',
	marginBlockEnd: '100px',
	width: '944px',
	height: '600px',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	borderRadius: '10px',
	overflow: 'hidden',
	boxShadow: '0px 18px 28px rgba(9, 30, 66, 0.13), 0px 0px 1px rgba(9, 30, 66, 0.31)',

	[media.below.md]: {
		display: 'none',
	},

	// Tech debt: Adding this disable to unblock commits for GWP-2646
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		cursor: 'pointer',
	},
});

const JswRoadmapTable = css({
	width: '908px',
	display: 'flex',
	margin: '0px 18px',
	flex: '10',
});

const JswRoadmapHeader = css({
	width: '908px',
	display: 'flex',
	flex: '10',
});

const HeaderWrapper = css({
	display: 'flex',
	flex: '1',
	margin: '0px 18px',
	justifyContent: 'space-between',
	flexDirection: 'row',
});
const staticImageContainer = css({
	display: 'none',
	[media.below.md]: {
		display: 'flex',
		position: 'relative',
		width: '100%',
		height: 'auto',
		justifyContent: 'center',
		alignItems: 'center',
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		img: {
			display: 'block',
			height: 'auto',
			maxWidth: '100%',
		},
	},
});
const staticSpotlightContainer = css({
	position: 'absolute',
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
});
